import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import Modal from '@minecraft.modal';
import FormikInput from '@minecraft.formik-input';
import { useTranslation, TranslateFn } from '@blocs.i18n';

const Hint = styled.p`
  margin-bottom: 1rem;
`;

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 0.875rem;
`;

const StyledFormikInput = styled(FormikInput)`
  max-width: 20rem;
`;

const getValidationSchema = (t: TranslateFn): any => {
  const schema = {
    text: Yup.string().trim().required(t('common:validation.fieldCannotBeBlank')),
  };

  return Yup.object().shape(schema);
};

type Props = {
  currentValue?: string;
  hintText?: string;
  submitLabel?: string;
  handleModalSubmit: (newValue: string) => void;
  handleModalToggle: () => void;
};

const UserNameModal = ({
  currentValue = '',
  hintText,
  submitLabel,
  handleModalSubmit,
  handleModalToggle,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Formik
      initialValues={{ text: currentValue }}
      validationSchema={getValidationSchema(t)}
      onSubmit={(values): void => {
        const newValue = values.text.trim();

        if (newValue === currentValue) {
          handleModalToggle();

          return;
        }

        handleModalSubmit(newValue);
      }}
    >
      {({ handleSubmit }): JSX.Element => (
        <Modal
          data-testid="user-name-modal"
          title={t('common:recipient.usernameModal.title')}
          modalSize="small"
          cancelLabel={t('common:button.cancel')}
          submitLabel={submitLabel || t('common:recipient.usernameModal.submitLabelDefault')}
          handleToggleClick={handleModalToggle}
          handleSubmitClick={handleSubmit}
          data-qa-id="recipient-name-modal"
        >
          <Form>
            <Hint>{hintText || t('common:recipient.usernameModal.hintTextDefault')}</Hint>
            <StyledLabel htmlFor="text">{t('common:form.label.yourNameRequired')}</StyledLabel>
            <StyledFormikInput showLimit maxLength={50} name="text" data-qa-id="recipient-name-modal-input" />
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default UserNameModal;
